// Shared classification for RTK Query errors. `status` here is whatever
// customBaseQuery/baseQuery.js normalizes onto `error.status` - either an
// HTTP status number, or one of RTK Query's own string literals
// ("FETCH_ERROR" | "TIMEOUT_ERROR" | "PARSING_ERROR" | "CUSTOM_ERROR").
export const ERROR_KINDS = {
  OFFLINE: "offline",
  NETWORK: "network",
  TIMEOUT: "timeout",
  UNAUTHORIZED: "unauthorized",
  FORBIDDEN: "forbidden",
  NOT_FOUND: "notFound",
  RATE_LIMIT: "rateLimit",
  SERVER: "server",
  VALIDATION: "validation",
  UNKNOWN: "unknown",
};

export const DEFAULT_MESSAGES = {
  [ERROR_KINDS.OFFLINE]: "You're offline. Check your internet connection and try again.",
  [ERROR_KINDS.NETWORK]: "We couldn't reach the server. Please try again.",
  [ERROR_KINDS.TIMEOUT]: "That took too long to respond. Please try again.",
  [ERROR_KINDS.UNAUTHORIZED]: "Your session has expired. Please sign in again.",
  [ERROR_KINDS.FORBIDDEN]: "You don't have permission to do that.",
  [ERROR_KINDS.NOT_FOUND]: "We couldn't find what you were looking for.",
  [ERROR_KINDS.RATE_LIMIT]: "You're doing that too much. Please wait a moment and try again.",
  [ERROR_KINDS.SERVER]: "Something went wrong on our end. Please try again shortly.",
  [ERROR_KINDS.VALIDATION]: "Some of the information provided isn't valid.",
  [ERROR_KINDS.UNKNOWN]: "Something went wrong. Please try again.",
};

export const classifyError = (status) => {
  if (status === "FETCH_ERROR") {
    return typeof navigator !== "undefined" && navigator.onLine === false
      ? ERROR_KINDS.OFFLINE
      : ERROR_KINDS.NETWORK;
  }
  if (status === "TIMEOUT_ERROR") return ERROR_KINDS.TIMEOUT;
  if (status === "PARSING_ERROR" || status === "CUSTOM_ERROR") return ERROR_KINDS.UNKNOWN;

  if (typeof status === "number") {
    if (status === 401) return ERROR_KINDS.UNAUTHORIZED;
    if (status === 403) return ERROR_KINDS.FORBIDDEN;
    if (status === 404) return ERROR_KINDS.NOT_FOUND;
    if (status === 429) return ERROR_KINDS.RATE_LIMIT;
    if (status === 400 || status === 422) return ERROR_KINDS.VALIDATION;
    if (status >= 500) return ERROR_KINDS.SERVER;
  }

  return ERROR_KINDS.UNKNOWN;
};

// Extracts the best human-readable message out of an RTK Query error shape,
// preferring server-supplied detail (validation arrays first, then a plain
// message) over the generic fallback passed in by the caller.
export const getApiErrorMessage = (error, fallback) => {
  if (Array.isArray(error?.data?.errors) && error.data.errors.length) {
    return error.data.errors.join("; ");
  }
  if (
    Array.isArray(error?.data?.validationErrors) &&
    error.data.validationErrors.length
  ) {
    return error.data.validationErrors.join("; ");
  }
  return error?.data?.message || error?.message || fallback;
};
